import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import LoadingDialog from "src/components/LoadingDialog";
import { getLimitationUsage } from "src/api/limitations";
import type { LimitationDefinition, LimitationUsageEntry } from "src/shared/api-types/limitations";
import { captureHandledException } from "src/services/sentry";

import { getDefinitionDisplayName } from "../limitationDisplay";

interface LimitationUsageDialogProps {
  crewId: number;
  definition: LimitationDefinition | null;
  onClose: () => void;
}

const LimitationUsageDialog = ({ crewId, definition, onClose }: LimitationUsageDialogProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<LimitationUsageEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedDefinitionId, setLoadedDefinitionId] = useState<number | null>(null);

  useEffect(() => {
    if (!definition) {
      setRows([]);
      setIsLoading(false);
      setLoadedDefinitionId(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setRows([]);
    setLoadedDefinitionId(null);

    getLimitationUsage({ crewId, limitationId: definition.id })
      .then((usageRows) => {
        if (cancelled) return;
        setRows(usageRows);
        setLoadedDefinitionId(definition.id);
      })
      .catch((err) => {
        if (cancelled) return;
        captureHandledException(err, {
          feature: "limitations",
          action: "loadUsage",
          crewId,
          limitationId: definition.id,
        });
        enqueueSnackbar(t("limitations.usage_load_failed"), { variant: "error" });
        onClose();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [crewId, definition, enqueueSnackbar, onClose, t]);

  if (!definition) {
    return null;
  }

  const isShowingLoading = isLoading || loadedDefinitionId !== definition.id;

  return (
    <LoadingDialog
      open
      onClose={onClose}
      loading={isShowingLoading}
      loadingMessage={t("limitations.loading_usage")}
      closeLabel={t("close")}
      title={t("limitations.usage_dialog_title", {
        limitation: getDefinitionDisplayName(definition, t),
      })}
    >
      {rows.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("limitations.no_usage")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("limitations.usage_user")}</TableCell>
                <TableCell>{t("limitations.usage_aircraft")}</TableCell>
                <TableCell>{t("limitations.usage_operation")}</TableCell>
                <TableCell>{t("limitations.usage_value")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.userId}-${row.aircraftTypeId}-${row.operationId}`}>
                  <TableCell>{row.userName}</TableCell>
                  <TableCell>{row.aircraftTypeName}</TableCell>
                  <TableCell>{row.operationName}</TableCell>
                  <TableCell>{row.displayValue}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </LoadingDialog>
  );
};

export default LimitationUsageDialog;
